import { deepObjectFreeze } from 'utils/deep-object-freeze/deep-object-freeze';

export const routes = deepObjectFreeze({
  '/login': '/login',
  '/forgotten-password': '/forgotten-password',
  '/error': '/error',
  '/sso-redirect': '/sso-redirect',
  '/mfa-setup': '/mfa-setup',
  '/session-expired': '/session-expired',
  '/sign-up': '/sign-up',
} as const);

export type RoutesValues = (typeof routes)[keyof typeof routes];

// routes where the user should be automatically redirected back to the client
// if the clientId is expired
export const routesWithClientId: RoutesValues[] = [
  routes['/login'],
  routes['/mfa-setup'],
  routes['/sign-up'],
];

export const testableRoutes = deepObjectFreeze({
  '/test-route': '/test-route',
});

export const routesWithNoLoginLink: RoutesValues[] = [routes['/sign-up']];

export const errorHTTPCodes = deepObjectFreeze({
  badRequest: 400,
  unauthorized: 401,
});

/*
Both these errors have the same status code (401) but different types and messages. 
See below example error responses from the server which are converted to a CustomError.
CustomError will report the first error message at top level.
So it's simplest to differentiate the cases based on the message.

Incorrect username or password will give this form of error:
{
    "trace_id": "136615110438647543064653924355585368464",
    "request_id": "68e40383-c9f2-45c8-b512-a65f0399b2d3",
    "errors": [
        {
            "type": "Unauthorized",
            "message": "Unauthorized",
            "attributes": {}
        }
    ],
    "server_time": "2024-08-22 11:11:57.576486"
}

// Account locked will give this form of error:
{
    "trace_id": "136615113449317718610970781487986642954",
    "request_id": "5ac32893-450f-477b-b855-e205450974bb",
    "errors": [
        {
            "type": "AccountLocked",
            "message": "Maximum login attempts exceeded",
            "attributes": {
                "locked_for_in_minutes": 30
            }
        }
    ],
    "server_time": "2024-08-22 11:12:33.277088"

    // Sign Up errors may look like this, with multiple errors in the array:
    [
    {
      "type": "InvalidLength",
      "message": "Field `first_name` cannot be longer than `100` characters.",
      "attributes": {
        "field_name": "first_name",
        "max_length": 100
      }
    },
    {
      "type": "RequiredField",
      "message": "Field `password` is required and was missing from the request.",
      "attributes": {
        "field_name": "password",
      }
    },
  ]
}
*/
// These are possible values of the 'type' field in the backend error response
// Not TypeScript types
export const loginBackendErrorTypes = deepObjectFreeze({
  incorrectUsernameOrPassword: 'Unauthorized',
  accountLocked: 'AccountLocked',
});

export const formErrorDelay = 800; // delay before showing error in forms
// otherwise MFA code input will show the minLength error as the user types
// similarly with an email address, we don't want to complain about the format while the user is still typing

// clientId actually lasts for 16 minutes but we want to allow time for network requests
export const clientExpiryMilliseconds = 1000 * 60 * 15; // 15 minutes
export const redirectionTimeoutDuration = 5000;
